import React, { useCallback, useMemo, useState } from "react";

import Alert from "veneer/Alert";
import Drawer from "veneer/Drawer";
import { showError, showSuccess } from "veneer/SnackBar";
import Stack from "veneer/Stack";
import { CheckFilledIcon } from "veneer/SvgIcon";
import { Colors } from "veneer/Theme";
import Tooltip from "veneer/Tooltip";
import Typography from "veneer/Typography";

import { Device } from "cortex/ui/src/fleet/view/device/model";
import { useAppDispatch } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas/actions";
import { CANCEL_UPGRADE } from "cortex/ui/src/redux-store/devices/actions";
import { hasPendingUpdate } from "cortex/ui/src/redux-store/devices/selectors";

import BaseDrawer, {
  ListItem,
  ListWrapper,
  UpgradeDrawerProps,
} from "./BaseDrawer";

export default function CancelPendingDrawer({
  onCancel,
  open,
  selectedItems,
  setSelectedItems,
}: UpgradeDrawerProps) {
  const dispatch = useAppDispatch();
  const [submitted, setSubmitted] = useState(false);
  const upgradeRows: ReadonlyArray<Device> = useMemo(() => {
    return selectedItems
      .filter(hasPendingUpdate)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedItems]);

  const validDeviceIds = useMemo(() => {
    return upgradeRows.map((row) => row.id);
  }, [upgradeRows]);

  const handleCancelUpgrade = useCallback(async () => {
    if (validDeviceIds.length === 0) {
      return;
    }
    setSubmitted(true);
    try {
      await createPromiseApiAction(dispatch, CANCEL_UPGRADE, {
        deviceIds: validDeviceIds,
      });
      showSuccess(
        "The request to cancel pending upgrades or downgrades was successful",
      );
    } catch (e) {
      showError("An error occurred while processing your request.");
    } finally {
      setSelectedItems([]);
      setSubmitted(false);
      onCancel();
    }
  }, [dispatch, onCancel, setSelectedItems, validDeviceIds]);

  const drawerHeader = (
    <Alert dense severity="warning" title="Double-check the device list">
      Devices that are offline or have installation issues will stay on their
      current version.
    </Alert>
  );

  return (
    <Drawer
      commitDisabled={validDeviceIds.length === 0 || submitted}
      commitLabel="Cancel Up/Downgrades"
      commitLoading={submitted}
      onCancel={onCancel}
      onClose={onCancel}
      onCommit={handleCancelUpgrade}
      open={open}
      padding={0}
      title="Cancel Pending Up/Downgrades"
    >
      <BaseDrawer drawerHeader={drawerHeader}>
        {upgradeRows.map((device) => (
          <ListWrapper disablePadding key={device.id}>
            <UpgradeListItem device={device} />
          </ListWrapper>
        ))}
      </BaseDrawer>
    </Drawer>
  );
}

const UpgradeListItem = ({ device }: { device: Device }) => {
  return (
    <ListItem>
      <Stack alignItems="center" direction="row" spacing={2}>
        <CheckFilledIcon color="success" />
        <Stack flex={1}>
          <Tooltip title={device.device_id}>
            <Typography color={Colors.gray1000} variant="body1">
              {device.name}
            </Typography>
          </Tooltip>
          <Typography variant="body2">
            Player: {device.current_version} → {device.target_version}
          </Typography>
        </Stack>
      </Stack>
    </ListItem>
  );
};
